import React, { useEffect, useMemo, useState } from "react";
import { getAllComplaints, deleteComplaint } from "../../services/complaintService";
import { Link } from "react-router-dom";

export default function AdminComplaintsList() {
  const STATUS = {
    FORWARDED: "forwarded",
    RECEIVED: "received",
    COMPLETED_SOLVED: "completed/solved",
    REJECTED: "rejected",
    IN_PROGRESS: "in progress",
  };
  const STATUS_FILTER = {
    ALL: "all",
    REJECTED: "rejected",
    COMPLETED: "completed",
    FORWARDED: "forwarded",
    RECEIVED: "received",
    IN_PROGRESS: "inProgress",
  };

  const [complaints, setComplaints] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER.ALL);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      const data = await getAllComplaints();
      setComplaints(data);
    } catch (err) {
      console.error(err);
      alert("Failed to load complaints.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this complaint?")) {
      try {
        await deleteComplaint(id);
        alert("Complaint deleted successfully!");
        fetchComplaints();
      } catch (err) {
        console.error(err);
        alert("Failed to delete complaint.");
      }
    }
  };

  const isComplaintInDateRange = (complaint, filterType) => {
    if (filterType === "all") return true;

    const complaintDateRaw =
      complaint.createdDate || complaint.CreatedDate || complaint.createdAt;
    if (!complaintDateRaw) return false;

    const complaintDate = new Date(complaintDateRaw);
    if (Number.isNaN(complaintDate.getTime())) return false;

    const now = new Date();
    if (filterType === "7d") {
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);
      return complaintDate >= sevenDaysAgo;
    }

    if (filterType === "1m") {
      const oneMonthAgo = new Date(now);
      oneMonthAgo.setMonth(now.getMonth() - 1);
      return complaintDate >= oneMonthAgo;
    }

    return true;
  };

  const shouldHighlightRow = (complaint) => {
    const status = (complaint.status || "").trim().toLowerCase();
    const isTargetStatus = status === STATUS.RECEIVED || status === STATUS.IN_PROGRESS;
    if (!isTargetStatus) return false;

    const complaintDateRaw =
      complaint.createdDate || complaint.CreatedDate || complaint.createdAt;
    if (!complaintDateRaw) return false;

    const complaintDate = new Date(complaintDateRaw);
    if (Number.isNaN(complaintDate.getTime())) return false;

    const now = new Date();
    const diffMs = now.getTime() - complaintDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    return diffDays > 15;
  };

  const getRowClassName = (complaint) => {
    const status = (complaint.status || "").trim().toLowerCase();
    if (status === STATUS.REJECTED) return "table-danger";
    if (shouldHighlightRow(complaint)) return "table-warning";
    return "";
  };

  const matchesStatusFilter = (complaint, filterType) => {
    if (filterType === STATUS_FILTER.ALL) return true;

    const status = (complaint.status || "").trim().toLowerCase();
    if (filterType === STATUS_FILTER.REJECTED) return status === STATUS.REJECTED;
    if (filterType === STATUS_FILTER.COMPLETED) {
      return status === STATUS.COMPLETED_SOLVED;
    }
    if (filterType === STATUS_FILTER.FORWARDED) return status === STATUS.FORWARDED;
    if (filterType === STATUS_FILTER.RECEIVED) return status === STATUS.RECEIVED;
    if (filterType === STATUS_FILTER.IN_PROGRESS) return status === STATUS.IN_PROGRESS;

    return false;
  };

  // Filter complaints based on selected date range + search term
  const filteredComplaints = complaints
    .filter((c) => isComplaintInDateRange(c, dateFilter))
    .filter((c) => matchesStatusFilter(c, statusFilter))
    .filter((c) => {
      const mobile = c.mobile || "";
      const applicationUniqueID = c.applicationUniqueID || "";
      return (
        mobile.includes(searchTerm) ||
        applicationUniqueID.toLowerCase().includes(searchTerm.toLowerCase())
      );
    })
    .sort((a, b) => {
      const aDateRaw = a.createdDate || a.CreatedDate || a.createdAt;
      const bDateRaw = b.createdDate || b.CreatedDate || b.createdAt;
      const aDate = aDateRaw ? new Date(aDateRaw).getTime() : 0;
      const bDate = bDateRaw ? new Date(bDateRaw).getTime() : 0;

      if (aDate !== bDate) return bDate - aDate;
      return (b.complaintID || 0) - (a.complaintID || 0);
    });

  const totalPages = Math.max(1, Math.ceil(filteredComplaints.length / pageSize));
  const currentSafePage = Math.min(currentPage, totalPages);
  const startIndex = (currentSafePage - 1) * pageSize;
  const paginatedComplaints = filteredComplaints.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const complaintStats = useMemo(() => {
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);

    const oneMonthAgo = new Date(now);
    oneMonthAgo.setMonth(now.getMonth() - 1);

    let last7DaysCount = 0;
    let lastOneMonthCount = 0;
    let rejectedCount = 0;
    let completedSolvedCount = 0;
    let forwardedCount = 0;
    let receivedCount = 0;
    let inProgressCount = 0;

    complaints.forEach((c) => {
      const status = (c.status || "").trim().toLowerCase();
      if (status === STATUS.REJECTED) rejectedCount += 1;
      if (status === STATUS.COMPLETED_SOLVED) {
        completedSolvedCount += 1;
      }
      if (status === STATUS.FORWARDED) forwardedCount += 1;
      if (status === STATUS.RECEIVED) receivedCount += 1;
      if (status === STATUS.IN_PROGRESS) inProgressCount += 1;

      const complaintDateRaw = c.createdDate || c.CreatedDate || c.createdAt;
      if (!complaintDateRaw) return;

      const complaintDate = new Date(complaintDateRaw);
      if (Number.isNaN(complaintDate.getTime())) return;

      if (complaintDate >= sevenDaysAgo) last7DaysCount += 1;
      if (complaintDate >= oneMonthAgo) lastOneMonthCount += 1;
    });

    return {
      total: complaints.length,
      last7Days: last7DaysCount,
      lastOneMonth: lastOneMonthCount,
      rejected: rejectedCount,
      completedSolved: completedSolvedCount,
      forwarded: forwardedCount,
      received: receivedCount,
      inProgress: inProgressCount,
    };
  }, [complaints]);

  const getMetricCardStyle = (accentColor, isActive) => ({
    cursor: "pointer",
    borderRadius: "14px",
    border: `1px solid ${isActive ? accentColor : "#e9ecef"}`,
    borderTop: `4px solid ${accentColor}`,
    background: isActive
      ? `linear-gradient(180deg, ${accentColor}15 0%, #ffffff 100%)`
      : "#ffffff",
    boxShadow: isActive
      ? "0 10px 24px rgba(0, 0, 0, 0.12)"
      : "0 4px 12px rgba(0, 0, 0, 0.08)",
    transform: isActive ? "translateY(-2px)" : "translateY(0)",
    transition: "all 0.2s ease",
  });

  const valueStyle = {
    fontSize: "1.8rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginBottom: 0,
  };

  const titleStyle = {
    fontSize: "0.86rem",
    fontWeight: 600,
    color: "#6c757d",
    marginBottom: "0.5rem",
  };

  const formatComplaintDate = (complaint) => {
    const dateRaw = complaint.appliedDate || complaint.AppliedDate || complaint.createdDate || complaint.CreatedDate || complaint.createdAt;
    if (!dateRaw) return "N/A";
    const parsed = new Date(dateRaw);
    return Number.isNaN(parsed.getTime()) ? "N/A" : parsed.toLocaleDateString();
  };

  const actionBtnStyle = {
    width: "34px",
    height: "34px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    padding: 0,
  };

  const tableCardStyle = {
    borderRadius: "16px",
    overflow: "hidden",
    border: "1px solid #e9ecef",
    boxShadow: "0 10px 25px rgba(13, 110, 253, 0.08)",
    background: "#ffffff",
  };

  const tableHeadCellStyle = {
    background: "linear-gradient(180deg, #1f2937 0%, #111827 100%)",
    color: "#f8fafc",
    fontWeight: 600,
    fontSize: "0.86rem",
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    borderBottom: "none",
    whiteSpace: "nowrap",
  };

  const searchWrapStyle = {
    border: "1px solid #dbe7ff",
    borderRadius: "14px",
    background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
    boxShadow: "0 8px 22px rgba(13, 110, 253, 0.08)",
  };

  const getStatusBadgeClass = (statusValue) => {
    const status = (statusValue || "").trim().toLowerCase();
    if (status === STATUS.COMPLETED_SOLVED) return "bg-success";
    if (status === STATUS.REJECTED) return "bg-danger";
    if (status === STATUS.FORWARDED) return "bg-primary";
    if (status === STATUS.RECEIVED) return "bg-warning text-dark";
    if (status === STATUS.IN_PROGRESS) return "bg-info text-dark";
    return "bg-secondary";
  };

  const blinkingBadgeStyle = {
    animation: "statusBlink 1s ease-in-out infinite",
  };

  return (
    <div className="container my-4">
      <style>
        {`
          @keyframes statusBlink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.35; }
          }
        `}
      </style>
      <h1 className="mb-4">Admin Complaints Dashboard</h1>

      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <div
            className="card h-100"
            onClick={() => {
              setDateFilter("all");
              setStatusFilter(STATUS_FILTER.ALL);
            }}
            style={getMetricCardStyle("#0d6efd", dateFilter === "all")}
          >
            <div className="card-body">
              <h6 style={titleStyle}>Total No. of Complaints</h6>
              <h3 style={valueStyle}>{complaintStats.total}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div
            className="card h-100"
            onClick={() => {
              setDateFilter("7d");
              setStatusFilter(STATUS_FILTER.ALL);
            }}
            style={getMetricCardStyle("#198754", dateFilter === "7d")}
          >
            <div className="card-body">
              <h6 style={titleStyle}>Total No. of Complaints Last 7 Days</h6>
              <h3 style={valueStyle}>{complaintStats.last7Days}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div
            className="card h-100"
            onClick={() => {
              setDateFilter("1m");
              setStatusFilter(STATUS_FILTER.ALL);
            }}
            style={getMetricCardStyle("#fd7e14", dateFilter === "1m")}
          >
            <div className="card-body">
              <h6 style={titleStyle}>Total No. of Complaints Last One Month</h6>
              <h3 style={valueStyle}>{complaintStats.lastOneMonth}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-2 mb-3">
          <div
            className="card h-100"
            onClick={() =>
              setStatusFilter((prev) => {
                const next = prev === STATUS_FILTER.REJECTED ? STATUS_FILTER.ALL : STATUS_FILTER.REJECTED;
                setDateFilter("all");
                return next;
              })
            }
            style={getMetricCardStyle("#dc3545", statusFilter === STATUS_FILTER.REJECTED)}
          >
            <div className="card-body">
              <h6 style={titleStyle}>Total No. of Rejected Complaints</h6>
              <h3 style={valueStyle}>{complaintStats.rejected}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-2 mb-3">
          <div
            className="card h-100"
            onClick={() =>
              setStatusFilter((prev) => {
                const next = prev === STATUS_FILTER.COMPLETED ? STATUS_FILTER.ALL : STATUS_FILTER.COMPLETED;
                setDateFilter("all");
                return next;
              })
            }
            style={getMetricCardStyle("#198754", statusFilter === STATUS_FILTER.COMPLETED)}
          >
            <div className="card-body">
              <h6 style={titleStyle}>Total No. of Completed/Solved Complaints</h6>
              <h3 style={valueStyle}>{complaintStats.completedSolved}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-2 mb-3">
          <div
            className="card h-100"
            onClick={() =>
              setStatusFilter((prev) => {
                const next = prev === STATUS_FILTER.FORWARDED ? STATUS_FILTER.ALL : STATUS_FILTER.FORWARDED;
                setDateFilter("all");
                return next;
              })
            }
            style={getMetricCardStyle("#0dcaf0", statusFilter === STATUS_FILTER.FORWARDED)}
          >
            <div className="card-body">
              <h6 style={titleStyle}>Total No. of Forwarded Complaints</h6>
              <h3 style={valueStyle}>{complaintStats.forwarded}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div
            className="card h-100"
            onClick={() =>
              setStatusFilter((prev) => {
                const next = prev === STATUS_FILTER.RECEIVED ? STATUS_FILTER.ALL : STATUS_FILTER.RECEIVED;
                setDateFilter("all");
                return next;
              })
            }
            style={getMetricCardStyle("#ffc107", statusFilter === STATUS_FILTER.RECEIVED)}
          >
            <div className="card-body">
              <h6 style={titleStyle}>Total No. of Received Complaints</h6>
              <h3 style={valueStyle}>{complaintStats.received}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb-3">
          <div
            className="card h-100"
            onClick={() =>
              setStatusFilter((prev) => {
                const next = prev === STATUS_FILTER.IN_PROGRESS ? STATUS_FILTER.ALL : STATUS_FILTER.IN_PROGRESS;
                setDateFilter("all");
                return next;
              })
            }
            style={getMetricCardStyle("#6c757d", statusFilter === STATUS_FILTER.IN_PROGRESS)}
          >
            <div className="card-body">
              <h6 style={titleStyle}>Total No. of In Progress Complaints</h6>
              <h3 style={valueStyle}>{complaintStats.inProgress}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Search Input */}
      <div className="mb-3 p-3 p-md-4" style={searchWrapStyle}>
        <label className="form-label fw-semibold text-primary mb-2">Quick Search</label>
        <div className="input-group input-group-lg">
          <span className="input-group-text bg-white border-end-0">
            <i className="fa fa-search text-secondary" aria-hidden="true"></i>
          </span>
          <input
            type="text"
            className="form-control border-start-0 shadow-none"
            placeholder="Search by Mobile or Application ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div style={tableCardStyle} className="mb-3">
        <div
          className="px-3 px-md-4 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
          style={{ background: "linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%)" }}
        >
          <h5 className="mb-0 fw-semibold text-primary">Complaints List</h5>
          <span className="badge bg-primary-subtle text-primary-emphasis fs-6">
            Total: {filteredComplaints.length}
          </span>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th style={tableHeadCellStyle}>ID</th>
                <th style={tableHeadCellStyle}>Application ID</th>
                <th style={tableHeadCellStyle}>Complainant</th>
                <th style={tableHeadCellStyle}>Email</th>
                <th style={tableHeadCellStyle}>Mobile</th>
                <th style={tableHeadCellStyle}>Applied Date</th>
                <th style={tableHeadCellStyle}>Status</th>
                <th style={tableHeadCellStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredComplaints.length > 0 ? (
                paginatedComplaints.map((c) => {
                  return (
                    <tr
                      key={c.complaintID}
                      className={getRowClassName(c)}
                      style={{ verticalAlign: "middle" }}
                    >
                      <td className="fw-semibold">{c.complaintID}</td>
                      <td>{c.applicationUniqueID}</td>
                      <td>{c.complainantName}</td>
                      <td>{c.email}</td>
                      <td>{c.mobile}</td>
                      <td>{formatComplaintDate(c)}</td>
                      <td>
                        <span
                          className={`badge ${getStatusBadgeClass(c.status)}`}
                          style={shouldHighlightRow(c) ? blinkingBadgeStyle : undefined}
                        >
                          {c.status}
                        </span>
                      </td>
                      <td>
                        <Link
                          to={`/admin/complaints/view/${c.complaintID}`}
                          className="btn btn-sm btn-outline-info me-2"
                          title="View"
                          aria-label="View complaint"
                          style={actionBtnStyle}
                        >
                          <i className="fa fa-eye" aria-hidden="true"></i>
                        </Link>
                        <Link
                          to={`/admin/complaints/edit/${c.complaintID}`}
                          className="btn btn-sm btn-outline-warning me-2"
                          title="Edit"
                          aria-label="Edit complaint"
                          style={actionBtnStyle}
                        >
                          <i className="fa fa-edit" aria-hidden="true"></i>
                        </Link>
                        <button
                          onClick={() => handleDelete(c.complaintID)}
                          className="btn btn-sm btn-outline-danger"
                          title="Delete"
                          aria-label="Delete complaint"
                          style={actionBtnStyle}
                        >
                          <i className="fa fa-trash" aria-hidden="true"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-4">
                    No complaints found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2">
        <p className="mb-0 text-muted">
          Showing{" "}
          {filteredComplaints.length === 0 ? 0 : startIndex + 1}
          -
          {Math.min(startIndex + pageSize, filteredComplaints.length)} of {filteredComplaints.length}
        </p>
        <div className="btn-group" role="group" aria-label="Pagination">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentSafePage === 1}
          >
            Previous
          </button>
          <button type="button" className="btn btn-outline-secondary" disabled>
            Page {currentSafePage} of {totalPages}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentSafePage === totalPages || filteredComplaints.length === 0}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}



